import { PDFTableColumn } from "components/PDF/Table";
import {
  VotacaoVotoIndividualItem,
  VotacaoVotosPorPerguntaItem,
} from "features/assembleia/typings";
import { Text } from "@react-pdf/renderer";
import moment from "moment";
import { useMemo, useCallback } from "react";
import { RelatorioVotosFields } from "core/features/relatorios/typings";
import { formatBlocoQuadra } from "infra/helpers/formatBlocoQuadra";

interface UsePDFRelatorioVotosColumnsProps {
  selectedFields: RelatorioVotosFields[];
}

export const usePDFRelatorioVotosColumns = ({
  selectedFields = [],
}: UsePDFRelatorioVotosColumnsProps) => {
  const shouldShow = useCallback(
    (field: RelatorioVotosFields) => selectedFields.includes(field),
    [selectedFields]
  );

  const { summaryTableColumns, detailTableColumns } = useMemo(() => {
    const summaryCols: PDFTableColumn<
      VotacaoVotosPorPerguntaItem["opcoes"][0]
    >[] = [];
    const detailCols: PDFTableColumn<VotacaoVotoIndividualItem>[] = [];

    if (shouldShow("opcaoTotalVotos")) {
      summaryCols.push({
        title: "Votos na Opção",
        render: (item: VotacaoVotosPorPerguntaItem["opcoes"][0]) => {
          return <Text>{item.totalVotos}</Text>;
        },
        textAlign: "center",
        flexGrow: 1,
      });
    }

    if (shouldShow("opcaoTotalPeso")) {
      summaryCols.push({
        title: "Peso na Opção",
        render: (item: VotacaoVotosPorPerguntaItem["opcoes"][0]) => {
          return <Text>{item.totalPeso}</Text>;
        },
        textAlign: "center",
        flexGrow: 1,
      });
    }

    if (shouldShow("votanteNome")) {
      detailCols.push({
        title: "Votante",
        render: (item: VotacaoVotoIndividualItem) => {
          return <Text>{item.pessoa.nome}</Text>;
        },
        flexGrow: 2,
      });
    }

    if (shouldShow("votanteUnidades")) {
      detailCols.push({
        title: "Unidade(s)",
        render: (item: VotacaoVotoIndividualItem) => {
          return (
            <Text>
              {item.unidades
                .map((u) =>
                  formatBlocoQuadra(u.numero, u.blocoQuadra?.nome, "")
                )
                .join("; ")}
            </Text>
          );
        },
        flexGrow: 4,
      });
    }

    if (shouldShow("votantePesoTotal")) {
      detailCols.push({
        title: "Peso",
        render: (item: VotacaoVotoIndividualItem) => {
          return <Text>{item.pesoTotal}</Text>;
        },
        textAlign: "right",
        flexGrow: 0.5,
      });
    }

    if (shouldShow("votanteDataVoto")) {
      detailCols.push({
        title: "Data/Hora",
        render: (item: VotacaoVotoIndividualItem) => {
          return (
            <Text>{moment(item.dataVoto).format("DD/MM/YYYY HH:mm")}</Text>
          );
        },
        textAlign: "right",
        flexGrow: 0.9,
      });
    }

    return { summaryTableColumns: summaryCols, detailTableColumns: detailCols };
  }, [shouldShow]);

  return { summaryTableColumns, detailTableColumns, shouldShow };
};
